import { useRef } from 'react';
import type { MouseEvent } from 'react';
import { motion, useMotionValue, useSpring, useTransform } from 'motion/react';
import { CursorImageTrail } from '@/components/special-effects/cursor-emoji/components/base/CursorImageTrail';

const trailImages = [
  'https://public.readdy.ai/ai/img_res/sandbox/0a9d2030d78e6980171043cc5a230587/587ec097-3f6f-459e-9fda-bbab1d2fb4a2_ff-trail-1.webp',
  'https://public.readdy.ai/ai/img_res/sandbox/0a9d2030d78e6980171043cc5a230587/587ec097-3f6f-459e-9fda-bbab1d2fb4a2_ff-trail-2.webp',
  'https://public.readdy.ai/ai/img_res/sandbox/0a9d2030d78e6980171043cc5a230587/587ec097-3f6f-459e-9fda-bbab1d2fb4a2_ff-trail-3.webp',
  'https://public.readdy.ai/ai/img_res/sandbox/0a9d2030d78e6980171043cc5a230587/587ec097-3f6f-459e-9fda-bbab1d2fb4a2_ff-trail-4.webp',
  'https://public.readdy.ai/ai/img_res/sandbox/0a9d2030d78e6980171043cc5a230587/587ec097-3f6f-459e-9fda-bbab1d2fb4a2_ff-trail-5.webp',
  'https://public.readdy.ai/ai/img_res/sandbox/0a9d2030d78e6980171043cc5a230587/587ec097-3f6f-459e-9fda-bbab1d2fb4a2_ff-trail-6.webp',
];

export const trailItems = trailImages.map((src) => (
  <img key={src} src={src} alt="Flower study" className="object-contain" draggable={false} />
));

interface ManifestoBodyProps {
  dark?: boolean;
  className?: string;
}

export function ManifestoBody({ dark = false, className = '' }: ManifestoBodyProps) {
  const ref = useRef<HTMLHeadingElement>(null);
  const px = useMotionValue(0);
  const py = useMotionValue(0);
  const sx = useSpring(px, { stiffness: 120, damping: 18, mass: 0.6 });
  const sy = useSpring(py, { stiffness: 120, damping: 18, mass: 0.6 });
  const rotateX = useTransform(sy, [-0.5, 0.5], [6, -6]);
  const rotateY = useTransform(sx, [-0.5, 0.5], [-8, 8]);

  const handleMove = (e: MouseEvent<HTMLHeadingElement>) => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    px.set((e.clientX - rect.left) / rect.width - 0.5);
    py.set((e.clientY - rect.top) / rect.height - 0.5);
  };
  const handleLeave = () => {
    px.set(0);
    py.set(0);
  };

  return (
    <CursorImageTrail
      items={trailItems}
      itemSize={110}
      trailLength={8}
      spawnDistance={80}
      rotationRange={18}
      className={className}
    >
      <div className="relative z-10 max-w-[1360px] mx-auto flex flex-col items-center text-center pointer-events-none">
        <div className="pointer-events-auto" style={{ perspective: 1200 }}>
          <motion.h2
            ref={ref}
            onMouseMove={handleMove}
            onMouseLeave={handleLeave}
            style={{ rotateX, rotateY, transformStyle: 'preserve-3d' }}
            className={`font-light tracking-[0.01em] leading-[1.25] text-[32px] sm:text-[44px] md:text-[56px] lg:text-[64px] will-change-transform ${dark ? 'text-[#f1ece2]' : ''}`}
          >
            Nature,<br />
            re-composed.
          </motion.h2>
        </div>
        <p className={`ff-serif italic text-[17px] md:text-[20px] mt-8 md:mt-12 ${dark ? 'text-[#f1ece2]/70' : 'text-black/60'}`}>
          A study in flowers and form.
        </p>
      </div>
    </CursorImageTrail>
  );
}

// Mobile-only standalone section; on desktop this content lives inside the pinned hero.
export default function Manifesto() {
  return (
    <section id="manifesto" className="bg-white md:hidden motion-reduce:md:block">
      <ManifestoBody className="w-full px-4 md:px-10 py-20 md:py-36" />
    </section>
  );
}